import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseSiteService, WindowRef } from '@spartacus/core';
import { ScriptConfig, ScriptStore } from './config/script-store.config';
import { EnvironmentService } from './environment.service';

type ScriptStatus = 'Loaded' | 'Already Loaded' | 'Not Applicable' | 'Script not found' | 'Loading in progress';

interface ScriptLoadResult {
  name: string;
  loaded: boolean;
  status: ScriptStatus;
}

@Injectable({
  providedIn: 'root',
})
export class ScriptService {
  private baseSiteService = inject(BaseSiteService);
  private environmentService = inject(EnvironmentService);
  private document = inject(DOCUMENT);
  private destroyRef = inject(DestroyRef);
  private winRef = inject(WindowRef);

  private scripts: ScriptConfig[] = ScriptStore;
  private loadQueue = new Set<string>();
  private loadedScripts = new Map<string, boolean>();

  private baseSiteId: string = '';
  private readonly activeEnvironment: 'stg' | 'prod';
  private readonly isDevMode: boolean;
  // Promise guard to ensure baseSite is available before any loading
  private baseSiteReady: Promise<string>;
  private resolveBaseSite!: (id: string) => void;

  constructor() {
    const env = this.environmentService.getCurrentEnvironment();
    this.activeEnvironment = env.production ? 'prod' : 'stg';
    this.isDevMode = !env.production;

    // Initialize readiness promise
    this.baseSiteReady = new Promise<string>((resolve) => {
      this.resolveBaseSite = resolve;
    });

    this.baseSiteService
      .getActive()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => {
        this.baseSiteId = id;
        this.resolveBaseSite(id);
      });
  }

  /**
   * Load a single script with optional query parameters
   * @param name - Script name from ScriptStore
   * @param attr - Optional query parameters as an array
   * @returns Promise resolving to load result
   */
  async loadScript(name: string, attr?: string[] | null): Promise<ScriptLoadResult> {
    // Guard: SSR
    if (!this.winRef.isBrowser()) {
      return Promise.resolve({
        name,
        loaded: false,
        status: 'Not Applicable',
      });
    }

    // Guard: if baseSite not yet resolved, delay until ready
    if (!this.baseSiteId) {
      return this.baseSiteReady.then(() => this.loadScript(name, attr));
    }

    return new Promise((resolve, reject) => {
      const script = this.scripts.find((script) => script.name === name);

      if (!script) {
        this.logScriptStatus(name, 'Script not found in store');
        resolve(this.createResult(name, false, 'Script not found'));
        return;
      }

      const scriptId = `${name}-${this.baseSiteId}`;

      // Check if script is applicable for current base site
      if (!script.baseSites.includes(this.baseSiteId)) {
        this.logScriptStatus(name, 'Not Applicable', `baseSite: ${this.baseSiteId}`);
        resolve(this.createResult(name, false, 'Not Applicable'));
        return;
      }

      // Check status (already loaded or loading)
      const statusCheck = this.checkScriptStatus(scriptId);
      if (statusCheck) {
        resolve(statusCheck);
        return;
      }

      this.loadQueue.add(scriptId);

      // Resolve dynamic URL (uniform function signature now)
      const scriptSrc = script.src(this.baseSiteId, this.activeEnvironment);

      this.createAndLoadScript(scriptId, scriptSrc, 'text/javascript', attr, undefined, resolve, reject);
    });
  }

  /**
   * Load script from template
   * @param name - Unique identifier for the script
   * @param template - Script content as string
   * @param scriptType - Script type attribute (default: 'module')
   * @returns Promise resolving to load result
   */
  loadScriptTemplate(
    name: string,
    template: string,
    scriptType: 'module' | 'text/javascript' = 'module'
  ): Promise<ScriptLoadResult> {
    return new Promise((resolve, reject) => {
      const scriptId = `${name}-${this.baseSiteId}`;

      // Check status (already loaded or loading)
      const statusCheck = this.checkScriptStatus(scriptId);
      if (statusCheck) {
        resolve(statusCheck);
        return;
      }

      this.loadQueue.add(scriptId);
      this.createAndLoadScript(scriptId, '', scriptType, [], template, resolve, reject);
    });
  }

  private createAndLoadScript(
    name: string,
    src: string,
    type: 'module' | 'text/javascript',
    attr: string[] | null | undefined,
    template: string | undefined,
    resolve: (value: ScriptLoadResult) => void,
    reject: (reason?: Error) => void
  ): void {
    const scriptElement = this.document.createElement('script');
    scriptElement.id = name;
    scriptElement.type = type;
    scriptElement.async = true;
    scriptElement.defer = true;

    const isInlineScript = template && template.length > 0;

    if (isInlineScript) {
      scriptElement.text = template;
    } else {
      const queryParams = attr && attr.length > 0 ? '?' + attr.join('&') : '';
      scriptElement.src = src + queryParams;
    }

    scriptElement.onerror = (error) => {
      this.loadQueue.delete(name);
      if (this.isDevMode) {
        console.error(`[ScriptService] ${name}: Failed to load`, error);
      }
      reject(new Error(`Failed to load script: ${name}`));
    };

    scriptElement.onload = () => {
      this.loadedScripts.set(name, true);
      this.loadQueue.delete(name);
      this.logScriptStatus(name, 'Loaded', isInlineScript ? 'inline template' : `src: ${src}`);
      resolve(this.createResult(name, true, 'Loaded'));
    };

    this.document.head.appendChild(scriptElement);
  }

  /**
   * Create a ScriptLoadResult object
   */
  private createResult(name: string, loaded: boolean, status: ScriptStatus): ScriptLoadResult {
    return { name, loaded, status };
  }

  /**
   * Check if script is already loaded or currently loading
   * @returns Result object if script should not be loaded, null if loading should proceed
   */
  private checkScriptStatus(name: string): ScriptLoadResult | null {
    // Check if already loaded (either in memory or DOM)
    if (this.loadedScripts.get(name)) {
      this.logScriptStatus(name, 'Already Loaded');
      return this.createResult(name, true, 'Already Loaded');
    }

    // Prevent duplicate loading attempts
    if (this.loadQueue.has(name)) {
      this.logScriptStatus(name, 'Loading in progress');
      return this.createResult(name, false, 'Loading in progress');
    }

    return null;
  }

  /**
   * Log script status in dev mode
   * @param name - Script name
   * @param status - Status message
   * @param details - Optional additional details
   */
  private logScriptStatus(name: string, status: string, details?: string): void {
    if (this.isDevMode) {
      const message = details
        ? `[ScriptService] ${name}: ${status} (${details})`
        : `[ScriptService] ${name}: ${status}`;
      console.log(message);
    }
  }
}
